import { getBrand } from "@cerberus/branding";
import { DEFAULT_MODULES_BY_BRAND } from "./defaults";
import { ModuleId } from "./types";

export * from "./types";
export { DEFAULT_MODULES_BY_BRAND } from "./defaults";

/** Every module id the platform knows about. */
export const ALL_MODULE_IDS: ModuleId[] = [
  "headache-questionnaire",
  "patient-self-signup",
  "questionnaires",
  "medication-refills",
  "billing",
  "out-of-area-interest",
  "provider-calendar",
  "patient-treatments",
  "patient-visits",
  "partner-infusions",
  "external-providers",
  "infusions",
  "clinic-locations",
  "caregivers",
  "questionnaire-builder",
];

/**
 * Modules that exist only in local development. They are never in a brand default, an explicit
 * override cannot turn them on in a deployed cell, and local development always has them —
 * so a deployment cannot leak one through config (the way runtime-config.local.js once leaked).
 * Mirrors the backend `ModuleCatalog.DevelopmentOnly`.
 */
export const DEVELOPMENT_ONLY_MODULES: readonly ModuleId[] = ["questionnaire-builder"];

export function isModuleId(value: unknown): value is ModuleId {
  return (
    typeof value === "string" && ALL_MODULE_IDS.includes(value as ModuleId)
  );
}

/**
 * Resolve the modules enabled for a deployment.
 *
 * - If `override` is provided (e.g. `window.runConfig.modules` or `MTC:Modules`),
 *   it wins — filtered to valid module ids. This decouples enablement from brand.
 * - Otherwise the brand's default set is used (so `th` and `mtc` differ out of the box).
 * - {@link DEVELOPMENT_ONLY_MODULES} are added when `isDevelopment`, and removed otherwise.
 */
export function resolveEnabledModules(
  brandKey?: string | null,
  override?: readonly string[] | null,
  isDevelopment = false,
): ModuleId[] {
  const base =
    override != null ? override.filter(isModuleId) : DEFAULT_MODULES_BY_BRAND[getBrand(brandKey).key];
  const deployed = base.filter((id) => !DEVELOPMENT_ONLY_MODULES.includes(id));
  return isDevelopment ? [...deployed, ...DEVELOPMENT_ONLY_MODULES] : deployed;
}
